import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, MapPin } from "lucide-react";
import { useDestinations, useCreateDestination, useUpdateDestination, useDeleteDestination } from "@/hooks/useDestinations";
import { Switch } from "@/components/ui/switch";

const SECURITY_BADGES: Record<string, { label: string; className: string }> = {
  padrao: { label: "Padrão", className: "bg-blue-50 text-blue-700 border-blue-200" },
  restrito: { label: "Restrito", className: "bg-amber-50 text-amber-700 border-amber-200" },
  critico: { label: "Crítico", className: "bg-red-50 text-red-700 border-red-200" },
  alta_seguranca: { label: "Alta Segurança", className: "bg-purple-50 text-purple-700 border-purple-200" },
};

const Destinos = () => {
  const { data: destinations = [], isLoading } = useDestinations();
  const createDestination = useCreateDestination();
  const updateDestination = useUpdateDestination();
  const deleteDestination = useDeleteDestination();

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [securityLevel, setSecurityLevel] = useState("padrao");

  const resetForm = () => {
    setName("");
    setLocation("");
    setSecurityLevel("padrao");
    setEditId(null);
  };

  const handleOpen = (dest?: any) => {
    if (dest) {
      setEditId(dest.id);
      setName(dest.name);
      setLocation(dest.location || "");
      setSecurityLevel(dest.security_level || "padrao");
    } else {
      resetForm();
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    if (editId) {
      await updateDestination.mutateAsync({ id: editId, name: name.trim(), location: location || undefined, security_level: securityLevel });
    } else {
      await createDestination.mutateAsync({ name: name.trim(), location: location || undefined, security_level: securityLevel });
    }
    setOpen(false);
    resetForm();
  };

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Destinos</h2>
            <p className="text-sm text-muted-foreground">Gerencie os departamentos e destinos disponíveis para visitantes</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpen()} className="gap-2">
                <Plus className="h-4 w-4" /> Novo Destino
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editId ? "Editar Destino" : "Novo Destino"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nome *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Recursos Humanos" />
                </div>
                <div>
                  <Label>Localização</Label>
                  <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="Ex: 2º Andar, Sala 201" />
                </div>
                <div>
                  <Label>Nível de Segurança</Label>
                  <Select value={securityLevel} onValueChange={setSecurityLevel}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="padrao">Padrão</SelectItem>
                      <SelectItem value="restrito">Restrito</SelectItem>
                      <SelectItem value="critico">Crítico</SelectItem>
                      <SelectItem value="alta_seguranca">Alta Segurança</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSubmit} disabled={!name.trim()} className="w-full">
                  {editId ? "Salvar Alterações" : "Criar Destino"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destino</TableHead>
                  <TableHead>Localização</TableHead>
                  <TableHead>Segurança</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : destinations.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum destino cadastrado</TableCell></TableRow>
                ) : (
                  destinations.map(d => {
                    const badge = SECURITY_BADGES[d.security_level] || SECURITY_BADGES.padrao;
                    return (
                      <TableRow key={d.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{d.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {d.location ? (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3" /> {d.location}
                            </div>
                          ) : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={d.is_active}
                            onCheckedChange={(checked) => updateDestination.mutate({ id: d.id, is_active: checked })}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleOpen(d)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteDestination.mutate(d.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Destinos;
